/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import {
  DATAITEM_TYPE_DIMENSION,
  COLUMNTYPE_TIMESTAMP,
  DATAITEM_TYPE_DERIVED_DIMENSION,
} from 'constants';

import { createSelector } from 'reselect';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import update from 'immutability-helper';

import { formatScopeForUI } from 'dataFormFunctions';
import {
  GET_DATAITEMS,
  GET_DATAITEMS_FAILURE,
  GET_DATAITEMS_SUCCESS,
  CLEAR_DATAITEMS,
  GET_FILTERED_DATAITEMS,
  GET_FILTERED_DATAITEMS_FAILURE,
  GET_FILTERED_DATAITEMS_SUCCESS,
  GET_DATAITEM_DETAILS,
  GET_DATAITEM_DETAILS_FAILURE,
  GET_DATAITEM_DETAILS_SUCCESS,
  GET_DATAITEM_GRAPH,
  GET_DATAITEM_GRAPH_FAILURE,
  GET_DATAITEM_GRAPH_SUCCESS,
  CLEAR_DATAITEM_DETAILS,
  GET_DATAITEM_RECENTDATA,
  GET_DATAITEM_RECENTDATA_FAILURE,
  GET_DATAITEM_RECENTDATA_SUCCESS,
} from 'dataItemActionCreators';
import { defaultFetchState } from 'reducerUtils';

// reducer
export const dataItemsReducer = (state = { data: [], fetchingData: false }, action) => {
  switch (action.type) {
    case GET_DATAITEMS:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
      };
    case GET_DATAITEMS_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case GET_DATAITEMS_SUCCESS: {
      // Find the subDataItems and make them children
      const dataItems = action.payload.map(dataItem => {
        const subDataItems = action.payload.filter(
          subDataItem => subDataItem.parentDataItemName === dataItem.name
        );
        return merge({}, dataItem, {
          subDataItems: isEmpty(subDataItems) ? null : subDataItems,
        });
      });

      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: dataItems,
      };
    }
    case CLEAR_DATAITEMS: {
      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: [],
      };
    }
    default:
      return state;
  }
};

// reducer
export const hierarchyDataItemsReducer = (state = { data: [], fetchingData: false }, action) => {
  switch (action.type) {
    case GET_FILTERED_DATAITEMS:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
      };
    case GET_FILTERED_DATAITEMS_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case GET_FILTERED_DATAITEMS_SUCCESS: {
      // Find the subDataItems and make them children
      const dataItems = action.payload.map(dataItem => {
        const subDataItems = action.payload.filter(
          subDataItem => subDataItem.parentDataItemName === dataItem.name
        );
        return merge({}, dataItem, {
          subDataItems: isEmpty(subDataItems) ? null : subDataItems,
        });
      });

      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: dataItems,
      };
    }
    default:
      return state;
  }
};

// reducer
export const dataItemDetailsReducer = (state = defaultFetchState, action) => {
  switch (action.type) {
    case GET_DATAITEM_DETAILS:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
      };
    case GET_DATAITEM_DETAILS_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: action.payload,
      };

    case GET_DATAITEM_DETAILS_SUCCESS:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: formatScopeForUI(action.payload),
      };
    case CLEAR_DATAITEM_DETAILS:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: {},
      };
    default:
      return state;
  }
};

const DATA_ITEMS_DATA = 'dataItems.data';
const FILTERED_DATA_ITEMS_DATA = 'hierarchyDataItems.data';

export const dataItemsSelector = state => get(state, DATA_ITEMS_DATA);
const hierarcyDataItemsSelector = state => get(state, FILTERED_DATA_ITEMS_DATA);

// TODO: check how to adapt entityTypeId in this selector when we are no longer using entityTypes, but hierarchy levels instead
/** lookup the timestamp column for an entity type for reuse in memoized selectors */
export const getTimestampColumnForEntityType = (state, props) => {
  if (!isEmpty(props)) {
    const {
      match: {
        params: { entityTypeId: entityType },
      },
    } = props;
    return get(state, ['entityTypes', 'data', entityType, 'metricTimestampColumn']);
  }
  return undefined;
};

/**
 * All data items (memoized)
 */
export const getAllDataItems = createSelector([dataItemsSelector], dataItems => dataItems || []);

/**
 * Merge data items and if it has sub data items
 * @param {*} dataItems
 * @returns
 */
const mergeDataItemsWithSubDataItems = (dataItems = []) =>
  isEmpty(dataItems)
    ? dataItems
    : dataItems.reduce((accumulatedDataItems, currentDataItem) => {
        const { subDataItems } = currentDataItem;

        if (!isEmpty(subDataItems)) {
          subDataItems.map(subDataItem => {
            const matchedDataItem = accumulatedDataItems.find(
              accumulatedDataItem =>
                accumulatedDataItem.name === subDataItem.name && // if data item name is same
                ((isNil(accumulatedDataItem.eventName) && isNil(subDataItem.eventName)) || // if eventName present then check that eventNames are same
                  accumulatedDataItem.eventName === subDataItem.eventName)
            );
            // if there is already data item in the accumalated data items then no need to add
            if (isNil(matchedDataItem)) {
              return accumulatedDataItems.push(subDataItem);
            }
            return accumulatedDataItems;
          });
        }

        return accumulatedDataItems;
      }, dataItems);

/**
 * All data items including their children
 */
export const getAllDataItemsWithChildren = createSelector([dataItemsSelector], dataItems =>
  mergeDataItemsWithSubDataItems(dataItems)
);

/**
 * All filtered data items including their children
 */
export const getAllHierarchyDataItemsWithChildren = createSelector(
  [hierarcyDataItemsSelector],
  dataItems => mergeDataItemsWithSubDataItems(dataItems)
);

export const getHierarchyDataItemsFetchingData = state =>
  get(state, 'hierarchyDataItems.fetchingData');

/**
 * filters out any timestamp, 'internal', or transient data items because they shouldn't be selectable in our dashboards or KPIs
 */
export const getGraphableDataItems = createSelector(
  [dataItemsSelector, getTimestampColumnForEntityType],
  (dataItems = [], timeStampColumn) =>
    dataItems.filter(
      dataItem =>
        dataItem.name !== 'ENTITY_ID' && // This is created by the ingestion process
        dataItem.name !== timeStampColumn &&
        dataItem.name !== 'rcv_timestamp_utc' && // This is created by the ingestion process
        !dataItem.transient // This is created by the "engine" so wouldn't make sense to a customer
    )
);

export const getDimensions = state =>
  get(state, DATA_ITEMS_DATA).filter(
    dataItem =>
      dataItem.columnType !== COLUMNTYPE_TIMESTAMP &&
      (dataItem.type === DATAITEM_TYPE_DIMENSION ||
        dataItem.type === DATAITEM_TYPE_DERIVED_DIMENSION)
  ) || [];

export const getDataItemsOfTypeColumnType = (state, type, columnType) =>
  get(state, DATA_ITEMS_DATA).filter(
    dataItem => dataItem.columnType === columnType && dataItem.type === type
  ) || [];

export const getDataItemsOfType = (state, type) =>
  get(state, DATA_ITEMS_DATA).filter(dataItem => dataItem.type === type) || [];

export const getDataItemsError = state => get(state, 'dataItems.fetchDataError');

export const getDataItemsFetchingData = state => get(state, 'dataItems.fetchingData');

export const getDataItemDetails = state => get(state, 'dataItemDetails.data') || {};

export const getDataItemDetailsError = state => get(state, 'dataItemDetails.fetchDataError');

export const getDataItemDetailsFetchingData = state => get(state, 'dataItemDetails.fetchingData');

// -----
// DataItem Graph Reducer
// -----
export const dataItemGraphReducer = (state = { data: {}, fetchingData: false }, action) => {
  switch (action.type) {
    case GET_DATAITEM_GRAPH:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
      };
    case GET_DATAITEM_GRAPH_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case GET_DATAITEM_GRAPH_SUCCESS:
      return update(state, {
        fetchingData: { $set: false },
        fetchDataError: { $set: null },
        data: { [action.dataItemName]: { $set: action.payload } },
      });
    default:
      return state;
  }
};

// DataItem Event Frequency Selectors
export const getDataItemGraphsFromStore = state => get(state, 'dataItemGraphs');

const selectFormState = state => get(state, 'form');
// get some form state
export const getFormDetails = createSelector([selectFormState], formState => formState || {});

// -----
// DataItem Sample Data Reducer
// -----
export const dataItemRecentDataReducer = (state = { data: {}, fetchingData: false }, action) => {
  switch (action.type) {
    case GET_DATAITEM_RECENTDATA:
      return {
        ...state,
        fetchingData: true,
        fetchDataError: null,
      };
    case GET_DATAITEM_RECENTDATA_FAILURE:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: action.payload,
      };
    case GET_DATAITEM_RECENTDATA_SUCCESS:
      return {
        ...state,
        fetchingData: false,
        fetchDataError: null,
        data: action.payload,
      };
    default:
      return state;
  }
};
export const getDataItemRecentData = state => get(state, 'dataItemRecentData.data');

export const getDataItemRecentDataFetching = state => get(state, 'dataItemRecentData.fetchingData');

export const getDataItemLastUpdated = state => {
  const recentData = getDataItemRecentData(state);
  return isEmpty(recentData) ? null : recentData[0].timestamp;
};
